// Global Instructions Rule Applied!
//
// Deal form Execution tab — Legal Terms read-through card (Phase 5, signed-off
// Mockup 5.3; replaces the Phase-5 placeholder). Same pattern as the cap-table
// card: compact live view, full management on the company form's Legal Rights
// tab. Shows this round's set's rights first, then other still-in-force
// company rights (labelled with their set); lapsed rows appear only when they
// belong to this round's set. Capped at a handful of rows with an "all
// rights" link.
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Table, Tag, Spin } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import {
  LegalAgreementSet,
  LegalRight,
  LegalRightsData
} from '../../../state_manager/data_models/legal_rights_model.ts'
import { LegalSetSavePayload, fetchLegalRights } from '../services/dealExecutionService.ts'
import LegalSetEditorModal, { LegalReferenceOption } from './LegalSetEditorModal.tsx'
import { RightStatusTag, SET_STATUS_COLORS, ruleText } from './LegalRightsTab.tsx'
import { pct } from './CapTableTab.tsx'

const MAX_ROWS = 6

interface LegalTermsCardProps {
  roundId: string | null | undefined
  companyId: string | null | undefined
  companyName: string | null | undefined
  dealId?: string | null
  /** Hands `refetch` up (to the Execution tab's composite) so the deal form's
   *  Refresh button and the refresh-on-activate pass drive this card too. */
  onRefreshReady?: ((refreshFn: () => Promise<void>) => void) | null
}

interface CardRow {
  set: LegalAgreementSet
  right: LegalRight
  fromThisRound: boolean
}

export default function LegalTermsCard({
  roundId,
  companyId,
  companyName,
  dealId,
  onRefreshReady = null
}: LegalTermsCardProps): React.ReactElement {
  const { openFormInTab } = useTabManagerContext()
  const [data, setData] = useState<LegalRightsData | null>(null)
  const [loading, setLoading] = useState(true)
  // Capture-in-place (owner-approved Option A, 2026-08-12): the header's
  // Capture button opens the existing set editor with this deal's round and
  // deal preselected; the card refetches on save (drafts show immediately).
  const [editorOpen, setEditorOpen] = useState(false)
  const [editorInitial, setEditorInitial] = useState<LegalSetSavePayload | null>(null)

  const refetch = useCallback(async (): Promise<void> => {
    if (!companyId) return
    const token = getAccessToken(store.getState().auth)
    const result = await fetchLegalRights(token, companyId)
    setData(result.data)
    setLoading(false)
  }, [companyId])

  useEffect(() => {
    if (!companyId) {
      setLoading(false)
      return
    }
    void refetch()
  }, [companyId, refetch])

  useEffect(() => {
    if (onRefreshReady) onRefreshReady(refetch)
  }, [onRefreshReady, refetch])

  const openCompanyLegalRights = (): void => {
    if (companyId) openFormInTab('companies', companyId, companyName || 'Company')
  }

  const openCapture = (): void => {
    if (!companyId) return
    setEditorInitial({
      account_id: companyId,
      round_id: roundId ?? null,
      deal_id: dealId ?? null,
      name: '',
      rights: []
    })
    setEditorOpen(true)
  }

  const referenceOptions: LegalReferenceOption[] = useMemo(() => {
    if (!data) return []
    return data.sets.flatMap((s) =>
      s.rights.map((r) => ({
        value: r.legal_rights__id,
        label: `${r.legal_rights__name} — ${s.legal_agreement_sets__name}`
      }))
    )
  }, [data])

  // This round's sets first (all their rights, incl. lapsed), then other
  // still-in-force company rights labelled with their set.
  const rows: CardRow[] = useMemo(() => {
    if (!data) return []
    const roundRows: CardRow[] = data.sets
      .filter((s) => roundId && s.rounds__id === roundId)
      .flatMap((s) => s.rights.map((right) => ({ set: s, right, fromThisRound: true })))
    const roundRightIds = new Set(roundRows.map((r) => r.right.legal_rights__id))
    const otherRows: CardRow[] = data.sets
      .filter((s) => !roundId || s.rounds__id !== roundId)
      .flatMap((s) =>
        s.rights
          .filter(
            (right) =>
              !roundRightIds.has(right.legal_rights__id) &&
              ['in_force', 'at_risk'].includes(right.evaluation.status || '')
          )
          .map((right) => ({ set: s, right, fromThisRound: false }))
      )
    return [...roundRows, ...otherRows]
  }, [data, roundId])

  // Header set: the round's newest set, else the company's latest executed one.
  const headerSet: LegalAgreementSet | null = useMemo(() => {
    if (!data) return null
    return (
      (roundId && data.sets.find((s) => s.rounds__id === roundId)) ||
      data.sets.find((s) => s.legal_agreement_sets__status === 'executed') ||
      null
    )
  }, [data, roundId])

  const totalRights = useMemo(
    () => (data ? data.sets.reduce((n, s) => n + s.rights.length, 0) : 0),
    [data]
  )

  return (
    <FormSection
      title='Legal Terms'
      titleColor='pink'
      scrollable={false}
      headerActions={
        companyId ? (
          <span className='flex items-center gap-2'>
            <button
              type='button'
              className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-xs'
              onClick={openCompanyLegalRights}
            >
              Open legal rights
            </button>
            <Button
              size='small'
              type='primary'
              icon={<PlusOutlined />}
              disabled={loading || !data}
              onClick={openCapture}
            >
              Capture set
            </Button>
          </span>
        ) : undefined
      }
    >
      {loading ? (
        <div className='flex justify-center py-6'>
          <Spin size='small' />
        </div>
      ) : !data || totalRights === 0 ? (
        <div className='text-sm text-gray-400 py-2'>
          No legal rights captured{companyName ? ` for ${companyName}` : ''} yet — capture the
          executed agreement set with the button above.
        </div>
      ) : (
        <div className='space-y-2'>
          <div className='flex items-center justify-between gap-2 text-xs text-gray-500 flex-wrap'>
            {headerSet ? (
              <span className='flex items-center gap-1'>
                <Tag
                  color={SET_STATUS_COLORS[headerSet.legal_agreement_sets__status] || 'default'}
                >
                  {headerSet.legal_agreement_sets__status}
                </Tag>
                {headerSet.legal_agreement_sets__name}
                {headerSet.legal_agreement_sets__executed_date
                  ? ` · executed ${dayjs(headerSet.legal_agreement_sets__executed_date).format('MM/DD/YYYY')}`
                  : ''}
              </span>
            ) : (
              <span />
            )}
            {data.position.our_pct_fd != null && (
              <span className='tabular-nums'>our {pct(data.position.our_pct_fd)} FD</span>
            )}
          </div>
          <Table
            size='small'
            rowKey={(row: CardRow) => row.right.legal_rights__id}
            pagination={false}
            dataSource={rows.slice(0, MAX_ROWS)}
            columns={[
              {
                title: 'Right',
                key: 'right',
                render: (_: unknown, row: CardRow) => (
                  <span>
                    <span className='font-medium'>{row.right.legal_rights__name}</span>
                    <div className='text-xs text-gray-400'>
                      {row.right.legal_rights__section_ref || row.right.legal_right_types__label || ''}
                      {!row.fromThisRound ? ` · from ${row.set.legal_agreement_sets__name}` : ''}
                    </div>
                  </span>
                )
              },
              {
                title: 'Basis',
                key: 'basis',
                width: '30%',
                render: (_: unknown, row: CardRow) => (
                  <span>
                    {row.right.legal_rights__basis === 'absolute' && <Tag>Absolute</Tag>}
                    {row.right.legal_rights__basis === 'rule' && <Tag color='purple'>Rule</Tag>}
                    {row.right.legal_rights__basis === 'right_reference' && (
                      <Tag color='blue'>References</Tag>
                    )}
                    {ruleText(row.right) && (
                      <div className='text-xs text-gray-500 tabular-nums mt-0.5'>
                        {ruleText(row.right)}
                      </div>
                    )}
                  </span>
                )
              },
              {
                title: 'Status',
                key: 'status',
                width: '24%',
                render: (_: unknown, row: CardRow) => <RightStatusTag right={row.right} />
              }
            ]}
          />
          {rows.length > MAX_ROWS && (
            <div className='text-xs text-gray-400'>
              {MAX_ROWS} of {rows.length} rights shown — in force &amp; this round&apos;s set
              first ·{' '}
              <span
                className='cursor-pointer text-blue-600 hover:underline'
                onClick={openCompanyLegalRights}
              >
                all rights
              </span>
            </div>
          )}
        </div>
      )}

      {companyId && data && editorOpen && (
        <LegalSetEditorModal
          open={editorOpen}
          accountId={companyId}
          initial={editorInitial}
          rightTypes={data.right_types}
          ruleVariables={data.rule_variables}
          companyRounds={data.company_rounds}
          companyDeals={data.company_deals}
          companyDocuments={data.company_documents}
          referenceOptions={referenceOptions}
          onRefreshRounds={refetch}
          onClose={() => setEditorOpen(false)}
          onSaved={async () => {
            setEditorOpen(false)
            await refetch()
          }}
        />
      )}
    </FormSection>
  )
}
